import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import { FormikProps } from "formik";
import CustomTextArea from "../../../../../../../../../components/CustomTextArea";
import Flex from "../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../components/Text";
import CustomTitle from "../../../../../../../../../components/app/CustomTitle";
import { useRoleController } from "../../../../../../../../../components/useRoleController";
import useDisclosure from "../../../../../../../../../hooks/use-disclosure";
import { useReduceDisclosure } from "../../../../../../../../../hooks/use-reduce-disclosure";
import { StyleAddButton } from "../../../../../../scheduling/appointment/encounter/default-forms/form-1/StyledAddButton";
import { Form2Payload } from "../../../../../../scheduling/appointment/encounter/encounter-constant";
import NewAppointment from "../../../../../../scheduling/appointment/NewAppointment";
import {
  AddIcdCodes,
  renderSelectedIcdCodes,
} from "../components/form-components/Add-ICD-Codes";
import { PsychiatricEvolutionForm } from "../components/form-components/InitialPsychotherapy/modals/PsychiatricEvolutionModal/PsychiatricEvolutionForm";
import RiskAssessmentModal from "../components/form-components/InitialPsychotherapy/modals/RiskAssessmentModal";
import PrimaryCareSoapNotePlan from "../components/form-components/primary-note/PrimaryCareSoapNotePlan";
import PsychoeducationTherapyForm from "./PsychoeducationTherapyForm";
import MentalStatusExaminationModal from "../components/form-components/InitialPsychotherapy/modals/MentalStatusExamination";
import {
  RiskAssessmentSummary,
  SelectedSectionsSummary,
} from "../components/form-components/InitialPsychotherapy/SelectedFormSummary";
import { PSYCHIATRIC_HISTORY_SECTIONS } from "../components/form-components/InitialPsychotherapy/modals/PsychiatricEvolutionModal/PsychiatricEvolutionFormConstant";
import { PHYSICAL_ASSESSMENT_EXAM_SECTIONS } from "../components/form-components/InitialPsychotherapy/InitialPsychotherapyConstant";

interface InitialPsychotherapyEvolutionProps {
  formik: FormikProps<Form2Payload>;
}

const InitialPsychotherapyEvolution = ({
  formik,
}: InitialPsychotherapyEvolutionProps) => {
  const riskAssessmentDisclosure = useDisclosure();
  const [icdDisclosure, psychiatricEvolutionDisclosure] =
    useReduceDisclosure(4);
  const physicalAssessmentDisclosure = useDisclosure();
  const appointmentDisclosure = useDisclosure();

  const { hasUpdate: hasUpdateAppointment } = useRoleController(
    "Appointment Scheduling"
  );

  return (
    <Flex p={1.5}>
      <Grid container spacing={2}>
        {/* Subjective section  */}
        <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle color={"primary"}>Subjective</CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            label="Chief Complaint"
            rows={3}
            name="subjectiveChiefComplaint"
            placeholder="Enter Chief Complaint"
            onBlur={formik.handleBlur}
            value={formik.values?.subjectiveChiefComplaint}
            onChange={formik.handleChange}
          />
        </Grid>

        <Grid size={12}>
          <CustomTextArea
            label="HPI"
            rows={3}
            name="historyOfPresentIllness"
            placeholder="Enter History of Present Illness"
            onBlur={formik.handleBlur}
            value={formik.values?.historyOfPresentIllness}
            onChange={formik.handleChange}
          />
        </Grid>

        {/* CG & HPI section */}
        {/* This field have independent API to save the data */}
        {/* <Grid item xs={12} md={12}>
          <Flex flexDirection="column" gap={1}>
            <CustomTitle>CC & HPI</CustomTitle>
          </Flex>
          <StyleAddButton
            w="100%"
            onClick={() => ccAndHpiDisclosure.open()}
            label="CC & HPI"
            centerLabel
          />
          <CCAndHPIUpsertForm
            formik={formik}
            disclosureHost={ccAndHpiDisclosure}
          />
        </Grid>
        <Grid item xs={6} md={6}>
          <Text mb={1}>Safety Plan Completed</Text>
          <CustomSelect
            name="safetyPlanCompleted"
            value={formik.values.safetyPlanCompleted}
            options={SAFETY_PLAN_COMPLETED_OPTIONS}
            onChange={(v) => formik.setFieldValue("safetyPlanCompleted", v)}
          />
        </Grid>
        <Grid item xs={6} md={6}>
          <Text mb={1}>Patient`s ID Verification Via</Text>
          <CustomSelect
            name="patientIdVerificationMethod"
            value={formik.values.patientIdVerificationMethod}
            options={PATIENT_ID_VERIFICATION_METHOD_OPTIONS}
            onChange={(v) =>
              formik.setFieldValue("patientIdVerificationMethod", v)
            }
          />
        </Grid> */}
        {/* psychiatric evalution section  */}
        {/* this field have independent API to save the data */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Psychiatric Evaluation</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => psychiatricEvolutionDisclosure.open()}
            label="Add Psychiatric Evaluation"
          />
          <PsychiatricEvolutionForm
            disclosureHost={psychiatricEvolutionDisclosure}
            formik={formik}
          />
          <SelectedSectionsSummary
            formik={formik}
            sections={PSYCHIATRIC_HISTORY_SECTIONS}
          />
        </Grid>
        <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle mt={3} color={"primary"}>
            Objective
          </CustomTitle>
        </Flex>
        {/* Physical Assessment section  */}
        {/* This field have independent API to save the data */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Mental Status Examination (MSE)</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => physicalAssessmentDisclosure.open()}
            label="Add Mental Status Examination (MSE)"
          />
          <MentalStatusExaminationModal
            disclosureHost={physicalAssessmentDisclosure}
            formik={formik}
          />
          <SelectedSectionsSummary
            formik={formik}
            sections={PHYSICAL_ASSESSMENT_EXAM_SECTIONS}
            overallNoteKey="overallMseNote"
            overallNoteLabel="Overall MSE Note"
          />
        </Grid>

        <Flex flexDirection="column" width={"100%"} gap={1} ml={2}>
          <CustomTitle my={1} color={"primary"}>
            Assessment
          </CustomTitle>
          <CustomTextArea
            rows={4}
            variant="outlined"
            name="assessmentFreeNote"
            placeholder="Assessment..."
            onBlur={formik.handleBlur}
            value={formik.values?.assessmentFreeNote || ""}
            onChange={formik.handleChange}
          />

          <Flex gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
            <StyleAddButton
              w="120px"
              onClick={() => icdDisclosure.open()}
              label="Add ICD Codes"
            />
            <AddIcdCodes formik={formik} disclosureHost={icdDisclosure} />
          </Flex>
          {!!formik.values?.assessmentCodes?.length && (
            <Flex flexDirection={"column"} width={"100%"} gap={1}>
              {/* <Text fontWeight={"bold"}>ICD Codes</Text> */}
              {renderSelectedIcdCodes(
                formik.values?.assessmentCodes ?? [],
                (code) => {
                  formik.setFieldValue("assessmentCodes", [
                    ...(formik.values?.assessmentCodes ?? []).filter(
                      (it) => it.value !== code.value
                    ),
                  ]);
                },
                "20vh"
              )}
            </Flex>
          )}
        </Flex>
        {/* Evaluation Findings section  */}
        {/* This field have independent API to save the data */}
        {/* <Grid item xs={12} md={12}>
          <CustomTitle mb={1}>Evaluation Findings:</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => evaluationFindingDisclosure.open()}
            label="Add Evaluation Findings"
          />
          <EvaluationFindingModal
            disclosureHost={evaluationFindingDisclosure}
          />
        </Grid> */}

        {/* Risk assessment section  */}
        {/* This field have independent API to save the data */}
        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <CustomTitle mb={1}>Risk Assessment:</CustomTitle>
          <StyleAddButton
            w="100%"
            centerLabel
            onClick={() => riskAssessmentDisclosure.open()}
            label="Add Risk Assessment"
          />
          <RiskAssessmentModal
            disclosureHost={riskAssessmentDisclosure}
            formik={formik}
          />
          <RiskAssessmentSummary formik={formik} />
        </Grid>

        {/* Safety Assessment (Suicide Risk): section  */}
        {/* <Flex flexDirection="column" width={"100%"} gap={1} ml={2}>
          <CustomTitle my={1}>Safety Assessment (Suicide Risk):</CustomTitle>
          <CustomSelect
            name="safetyAssessmentSuicideRisk"
            value={formik.values.safetyAssessmentSuicideRisk}
            options={SAFETY_ASSESSMENT_SUICIDE_RISK_OPTIONS}
            onChange={(v) =>
              formik.setFieldValue("safetyAssessmentSuicideRisk", v)
            }
          /> */}
        {/* <Flex flexDirection="column" gap={1}>
            <CustomTitle>
              Studies, Scans, Labwork (Date and Results for Review):
            </CustomTitle>
          </Flex>
          <Grid item xs={12}>
            <CustomTextArea
              label="Review"
              rows={3}
              name="studiesScansLabworkReview"
              placeholder="Enter Studies, Scans, Labwork (Date and Results for Review)"
              onBlur={formik.handleBlur}
              value={formik.values?.studiesScansLabworkReview}
              onChange={formik.handleChange}
            />
          </Grid> */}
        {/* </Flex> */}
        {/* Psychiatric Evolution Plan section  */}
        <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle mt={3} color={"primary"}>
            Plan
          </CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            label="Plan Note"
            rows={3}
            name="planFreeNote"
            placeholder="Plan..."
            onBlur={formik.handleBlur}
            value={formik.values?.planFreeNote || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid size={12}>
          <PrimaryCareSoapNotePlan
            formik={formik}
            showSendAssessment
            showAssignEducation
            showTherapies
          />
        </Grid>
        {/* <Grid item xs={12} md={12}></Grid>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToPsychiatricEvolutionplanOne"
                  checked={formik.values?.agreedToPsychiatricEvolutionplanOne}
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToPsychiatricEvolutionplanOne",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  Recommended that patient seek therapy, as recommended for best
                  practice, and for the best outcomes/results, in combination
                  with medication treatment. Therapy Resources Provided. Patient
                  counselled on remaining substance free.
                </Text>
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToPsychiatricEvolutionplanTwo"
                  checked={formik.values?.agreedToPsychiatricEvolutionplanTwo}
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToPsychiatricEvolutionplanTwo",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  No PCP:Diagnosis, symptoms, presentation, relapse, and
                  criteria explained. Patient also given credible information on
                  resources for further self-empowerment. Patient Currently DOES
                  NOT HAVE a PCP, and patient will be seen for Maintenance
                  therapy until she is able to secure a PCP, and establish. At
                  this time, she was encouraged to present current diagnosis and
                  treatment plan with receiving provider for all recommended
                  routine preventative care.
                </Text>
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToPsychiatricEvolutionplanThree"
                  checked={formik.values?.agreedToPsychiatricEvolutionplanThree}
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToPsychiatricEvolutionplanThree",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  Yes PCP:Diagnosis, symptoms, presentation, relapse, and
                  criteria explained. Patient also given credible information on
                  resources for further self-empowerment. Currently has a PCP.
                  The patient should discuss this diagnosis and treatment plan
                  with PCP as well, as we don't want to have overriding and/or
                  conflicting plans. Encouraged to present for all recommended
                  routine preventative care. At this time, she was encouraged to
                  present current diagnosis and treatment plan with primary care
                  provider for all recommended routine preventative care
                </Text>
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToPsychiatricEvolutionplanFour"
                  checked={formik.values?.agreedToPsychiatricEvolutionplanFour}
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToPsychiatricEvolutionplanFour",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  Smoking Cessation counseling done, alcohol abstinence
                  counseling also done during this visit.
                </Text>
              }
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextArea
              label="Plan Review"
              rows={3}
              name="psychiatricEvolutionPlanReview"
              placeholder="Enter Review"
              onBlur={formik.handleBlur}
              value={formik.values?.psychiatricEvolutionPlanReview}
              onChange={formik.handleChange}
            />
          </Grid>
        </Flex>
        {/* Mental Health Screening section  */}
        {/* <Grid item xs={12} md={6} lg={4}>
          <Text my={1}>Mental Health Screening</Text>
          <CustomDatePicker
            name="mentalHealthScreeningDate"
            value={formik.values.mentalHealthScreeningDate}
            onChange={(val) =>
              formik.setFieldValue("mentalHealthScreeningDate", val)
            }
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <Text my={1}>Interim</Text>
          <CustomDatePicker
            name="interimDate"
            value={formik.values.interimDate}
            onChange={(val) => formik.setFieldValue("interimDate", val)}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <Text my={1}>Update Weight</Text>
          <CustomDatePicker
            name="updateWeightDate"
            value={formik.values.updateWeightDate}
            onChange={(val) => formik.setFieldValue("updateWeightDate", val)}
          />
        </Grid>
        {/* Clinical Instuctions: */}
        {/* <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle mt={3}>Clinical Instuctions:</CustomTitle>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToClinicalInstructionsplanOne"
                  checked={formik.values?.agreedToClinicalInstructionsplanOne}
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToClinicalInstructionsplanOne",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  Patient educated about symptoms, illness and diagnosis.
                  Treatment options reviewed. Patient verbalized understanding
                  and agreement with treatment plan/recommendations. Patient
                  counseled on drug therapy, and medication adherence, as well
                  as when to seek emergency help.
                </Text>
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToClinicalInstructionsplanTwo"
                  checked={formik.values?.agreedToClinicalInstructionsplanTwo}
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToClinicalInstructionsplanTwo",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  Patient provided with information for suicide prevention,
                  including the national 9-8-8 toll free number, suicide hotline
                  information (Call: 1-800-273-TALK (1-800-273-8255) or Text:
                  "HOME" to 741741), information about contacting Emergency
                  Services, as well as when to seek emergency help.
                </Text>
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToClinicalInstructionsplanThree"
                  checked={formik.values?.agreedToClinicalInstructionsplanThree}
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToClinicalInstructionsplanThree",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  Recommended that patient seek therapy, as recommended for best
                  practice, and for the best outcomes/results, in combination
                  with medication treatment. Patient provided with therapy
                  resources. Recommended that patient seek therapy only at this
                  time, as medications are not appropriate in the management of
                  their current condition.
                </Text>
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToClinicalInstructionsplanFour"
                  checked={formik.values?.agreedToClinicalInstructionsplanFour}
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToClinicalInstructionsplanFour",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  Tier 1: Patient to return safety plan within 30 days (Initial
                  visit)
                </Text>
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToClinicalInstructionsplanFive"
                  checked={formik.values?.agreedToClinicalInstructionsplanFive}
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToClinicalInstructionsplanFive",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  ~NOTE: ~Patient requesting accommodations at work to
                  compensate with clinical symptoms associated with his/her
                  current condition(s)
                </Text>
              }
            />
          </Grid>
          <Text>Clinical Instructions Review</Text>
          <Grid item xs={12}>
            <CustomTextArea
              rows={3}
              name="clinicalInstructionsReview"
              placeholder="Enter Review"
              onBlur={formik.handleBlur}
              value={formik.values?.clinicalInstructionsReview}
              onChange={formik.handleChange}
            />
          </Grid>
        </Flex>
        {/* Adjunct Therapy Recommendations:: */}
        {/* <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle mt={3}>Adjunct Therapy Recommendations:</CustomTitle>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToAdjunctTherapyRecommendationsOne"
                  checked={
                    formik.values?.agreedToAdjunctTherapyRecommendationsOne
                  }
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToAdjunctTherapyRecommendationsOne",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  Trauma Therapy - To recognizes and emphasizes understanding
                  how the traumatic experience impacts a child's mental,
                  behavioral, emotional, physical, and spiritual well-being.
                </Text>
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToAdjunctTherapyRecommendationsTwo"
                  checked={
                    formik.values?.agreedToAdjunctTherapyRecommendationsTwo
                  }
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToAdjunctTherapyRecommendationsTwo",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  Dialectical behavior therapy (DBT)- To teach how to live in
                  the moment, develop healthy ways to cope with stress, regulate
                  emotions, and improve relationships with others.
                </Text>
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToAdjunctTherapyRecommendationsThree"
                  checked={
                    formik.values?.agreedToAdjunctTherapyRecommendationsThree
                  }
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToAdjunctTherapyRecommendationsThree",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  Cognitive behavioral therapy (CBT) - Short-term form of
                  behavioral treatment. Help problem-solve and reveals the
                  relationship between beliefs, thoughts, and feelings, and the
                  behaviors that follow.
                </Text>
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToAdjunctTherapyRecommendationsFour"
                  checked={
                    formik.values?.agreedToAdjunctTherapyRecommendationsFour
                  }
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToAdjunctTherapyRecommendationsFour",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>Talk Therapy - To get support through challenges</Text>
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToAdjunctTherapyRecommendationsFive"
                  checked={
                    formik.values?.agreedToAdjunctTherapyRecommendationsFive
                  }
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToAdjunctTherapyRecommendationsFive",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  Mental health Forum/Chat - Interact with like-minded
                  individuals and talk to lay persons with similar experiences
                  to help through tough times, using skills/tips/tricks derived
                  from experience.
                </Text>
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToAdjunctTherapyRecommendationsSix"
                  checked={
                    formik.values?.agreedToAdjunctTherapyRecommendationsSix
                  }
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToAdjunctTherapyRecommendationsSix",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  Social Groups/Events - Improve ability to recover from stress,
                  anxiety, and depression. Promote healthy eating, physical
                  activity, and weight. Improve sleep, well-being, and quality
                  of life. Reduce risk of violent and suicidal behaviors.
                  Prevent death from chronic diseases. Increase happiness.
                  Improve physical health. Improve cognitive functioning.
                </Text>
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToAdjunctTherapyRecommendationsSeven"
                  checked={
                    formik.values?.agreedToAdjunctTherapyRecommendationsSeven
                  }
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToAdjunctTherapyRecommendationsSeven",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  Light therapy- Natural light (outside/open window, etc) 30
                  minutes a day, trigger the production of the brain chemical
                  serotonin, resets the circadian rhythm of melatonin and
                  acutely inhibits melatonin synthesis, improvements have been
                  found in alertness, mood, energy, attention, concentration,
                  happiness, and other markers of depression (including
                  depression associated with mood disorders, and seasonal
                  affective disorder).
                </Text>
              }
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agreedToAdjunctTherapyRecommendationsEight"
                  checked={
                    formik.values?.agreedToAdjunctTherapyRecommendationsEight
                  }
                  onChange={(e) => {
                    formik.setFieldValue(
                      "agreedToAdjunctTherapyRecommendationsEight",
                      e.target.checked
                    );
                  }}
                />
              }
              label={
                <Text>
                  ABA Therapy - Change Behaviors, and ideal for Autism as well
                  as individuals with learning disabilities.
                </Text>
              }
            />
          </Grid>
        </Flex>
        {/* Individual Treatment/Therapy Requirements: */}
        {/* <Grid item xs={12}>
          <CustomTitle mb={2}>
            Individual Treatment/Therapy Requirements:
          </CustomTitle>
          <CustomTextArea
            rows={3}
            name="individualTreatmentTherapyRequirements"
            onBlur={formik.handleBlur}
            value={formik.values?.individualTreatmentTherapyRequirements}
            onChange={formik.handleChange}
          />
        </Grid> */}
        {/* Patient Goals (Time frame, duration of treatment, termination criteria): */}
        {/* <Grid item xs={12}>
          <CustomTitle mb={2}>
            Patient Goals (Time frame, duration of treatment, termination
            criteria):
          </CustomTitle>
          <CustomTextArea
            rows={3}
            name="patientGoals"
            placeholder="Enter Goals"
            onBlur={formik.handleBlur}
            value={formik.values?.patientGoals}
            onChange={formik.handleChange}
          />
        </Grid>
        <Flex flexDirection="column" gap={1} ml={2} mt={2}>
          <CustomTitle>Weight loss therapies:</CustomTitle>
          <RadioForWeight
            value={formik.values?.weightLossTherapiesOption ?? ""}
            onChange={formik.handleChange}
            name="weightLossTherapiesOption"
          />
          <CustomTitle>Weight loss therapies description:</CustomTitle>
          <CustomTextArea
            rows={3}
            name="weightLossTherapiesDescription"
            placeholder="Enter Therapies Description"
            onBlur={formik.handleBlur}
            value={formik.values?.weightLossTherapiesDescription}
            onChange={formik.handleChange}
          />
          <CustomTitle>Weight loss therapies:</CustomTitle>
          <WeightlossDurationRadio
            value={formik.values?.weightLossTherapiesDuration ?? ""}
            onChange={formik.handleChange}
            name="weightLossTherapiesDuration"
          />
          <CustomTitle>Weight loss therapies duration description:</CustomTitle>
          <CustomTextArea
            rows={3}
            name="weightLossTherapiesDurationDescription"
            placeholder="Enter Therapies Duration Description"
            onBlur={formik.handleBlur}
            value={formik.values?.weightLossTherapiesDurationDescription}
            onChange={formik.handleChange}
          />
        </Flex>

        <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle mt={3}>
            Forms to be provided to the patient/parent/guardian following
            current visit:
          </CustomTitle>

          {/* Multi-checkbox group for forms */}
        {/* <Grid item xs={12}>
            <CommonCheckboxGroup
              items={FormsToBeProvidedToFollowingCurrentVisitCheckboxItems}
              formik={formik}
            />
          </Grid>
        </Flex>
        <Grid item xs={12}>
          <CustomTitle mb={2}>Enter Note</CustomTitle>
          <CustomTextArea
            rows={3}
            name="noteForSendPatientForms"
            onBlur={formik.handleBlur}
            value={formik.values?.noteForSendPatientForms}
            onChange={formik.handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <CustomTitle mb={2}>Other Forms Provided:</CustomTitle>
          <CustomTextArea
            rows={3}
            name="otherFormsProvided"
            placeholder="Enter Note"
            onBlur={formik.handleBlur}
            value={formik.values?.otherFormsProvided}
            onChange={formik.handleChange}
          />
        </Grid>  */}
        {/* <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle my={2}>Patient Requests:</CustomTitle>

          {/* Multi-checkbox group for forms */}
        {/* <CommonCheckboxGroup
            items={PatientRequestsCheckboxItems}
            formik={formik}
          />  */}
        {/* </Flex>  */}
        {/* 
        <Grid item xs={12}>
          <CustomTitle>Other requests note:</CustomTitle>
          <CustomTextArea
            rows={3}
            name="otherRequestsNote"
            placeholder="Enter Note"
            onBlur={formik.handleBlur}
            value={formik.values?.otherRequestsNote}
            onChange={formik.handleChange}
          />
        </Grid> */}

        {/* Orders, Prescriptions, Lab requisition, CPT and Therapies are all
            rendered inside <PrimaryCareSoapNotePlan/> above. The standalone
            duplicate block was removed (ECRH-5279). */}

        {/* Follow Up & Instructions section */}
        <Flex flexDirection="column" gap={1} ml={2}>
          <CustomTitle mt={3} color={"primary"}>
            Follow Up & Instructions
          </CustomTitle>
        </Flex>
        <Grid size={12}>
          <CustomTextArea
            label="Follow Up & Instructions"
            rows={3}
            name="followUp"
            placeholder="Follow Up & Instructions..."
            onBlur={formik.handleBlur}
            value={formik.values?.followUp || ""}
            onChange={formik.handleChange}
          />
        </Grid>
        {hasUpdateAppointment && (
          <Grid size={12}>
            <Flex gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
              <StyleAddButton
                w="200px"
                onClick={() => {
                  appointmentDisclosure.open();
                }}
                label="Schedule F/U Appointment"
              />
              {appointmentDisclosure.opened && (
                <NewAppointment
                  enablePrefillPatientData
                  opened={appointmentDisclosure.opened}
                  onClose={appointmentDisclosure.close}
                />
              )}
            </Flex>
          </Grid>
        )}
        <Grid size={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="psychoeducationSessionRequired"
                checked={!!formik.values?.psychoeducationSessionRequired}
                onChange={(e) =>
                  formik.setFieldValue(
                    "psychoeducationSessionRequired",
                    e.target.checked
                  )
                }
              />
            }
            label={
              <Text variant="body1" fontWeight={500}>
                Psychoeducation Session Required?
              </Text>
            }
          />
        </Grid>
        {formik.values?.psychoeducationSessionRequired && (
          <Grid size={12}>
            <PsychoeducationTherapyForm formik={formik} />
          </Grid>
        )}
      </Grid>
    </Flex>
  );
};

export default InitialPsychotherapyEvolution;
